/**
 * Drive-only deployment (drive-app.xavior.ai) feature scoping.
 *
 * The drive deployment is a stripped-down, Drive-focused build served from the
 * `deploy/drive` branch. It hides a set of non-Drive modules from the rail +
 * conversation sidebar and skips the in-app onboarding tour (OnboardingBeats).
 *
 * Scoped either by the `VITE_DRIVE_ONLY` build flag (set on the Cloudflare
 * Pages `deploy/drive` build) OR, as a zero-config fallback, by the
 * `drive-app.xavior.ai` hostname at runtime — mirroring the existing hostname
 * checks in `lib/env.ts` (`app.xavior.ai` / `demo.xavior.ai`).
 */

/** True when this build/host is the drive-only deployment. */
export function isDriveOnlyDeployment(): boolean {
  if ((import.meta.env.VITE_DRIVE_ONLY as string | undefined) === 'true') return true
  if (
    typeof window !== 'undefined' &&
    window.location.hostname === 'drive-app.xavior.ai'
  ) {
    return true
  }
  return false
}

/**
 * Rail / sidebar-visibility keys hidden on the drive deployment:
 * Agents, Synthesis, Notes, Places, Health, Shopify, Mixpanel. These are the
 * `SidebarRail` visibility keys (core rail item keys + plugin visibility keys).
 */
export const DRIVE_HIDDEN_RAIL_KEYS: ReadonlySet<string> = new Set([
  'agents',
  'synthesis',
  'notes',
  'places',
  'health',
  'shopify',
  'mixpanel',
])

/**
 * Module INSTANCE types hidden on the drive deployment — the module `type`
 * strings behind the hidden rail keys (see `RAIL_MODULE_TYPES`). Used to filter
 * the conversation-sidebar "Modules" list so a seeded instance (e.g. a `notes`
 * or `places` module) does not surface even though the rail button is hidden.
 */
export const DRIVE_HIDDEN_MODULE_TYPES: ReadonlySet<string> = new Set([
  'agents',
  'synthesis',
  'notes',
  'places',
  'place_list',
  'health',
  'shopify-crm',
  'mixpanel',
  'app-analytics',
])

/** Should this rail visibility key be hidden on the current deployment? */
export function isDriveHiddenRailKey(key: string): boolean {
  return isDriveOnlyDeployment() && DRIVE_HIDDEN_RAIL_KEYS.has(key)
}

/** Should this module instance type be hidden on the current deployment? */
export function isDriveHiddenModuleType(type: string): boolean {
  return isDriveOnlyDeployment() && DRIVE_HIDDEN_MODULE_TYPES.has(type)
}
